import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import { format } from 'date-fns';
import { toast } from 'sonner';

import { useAuthStore } from '@/features/auth/model/auth-store';

import { refreshAfterStatusPush } from '@/shared/api/generation-cache';
import {
  type GenerationRowsCache,
  generationService,
  mapCachedGenerations,
} from '@/shared/api/generation-service';
import { useWebSocketMessages } from '@/shared/hooks/use-websocket-messages';
import { isStoryInference } from '@/shared/lib/story-inferences';
import { useGenerationStageStore } from '@/shared/stores/generation-stage-store';

/**
 * True when the playground history shows this generation's card (or, if that
 * card is not rendered, the newest card) inside the viewport. The history list
 * does not scroll itself (the page does), so this asks where the card sits on
 * screen. No list rendered yet (the page just loaded) counts as visible, so a
 * fresh load stays quiet.
 */
function isGenerationCardOnScreen(id: number | undefined): boolean {
  const list = document.querySelector('[data-generations-list]');
  if (!list) return true;
  const card =
    (id != null ? list.querySelector(`[data-generation-id="${id}"]`) : null) ??
    list.querySelector('[data-history-index="0"]');
  if (!card) return false;
  const rect = card.getBoundingClientRect();
  return rect.bottom > 0 && rect.top < window.innerHeight;
}

/**
 * Global inference notification listener.
 * Shows toast when a generation completes or fails, unless the user
 * is already viewing the latest generation on the playground page.
 */
export const InferenceNotifications = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const handleMessage = useCallback(
    (message: unknown) => {
      const data = message as {
        inference?: {
          id?: number;
          status?: string;
          outputUrls?: string[];
          errorMessage?: string;
          mediaType?: 'image' | 'video';
          /**
           * Per-output streaming events from the backend dispatch loop. One
           * event per newly-complete Lumi child carries a single
           * `{ index, url }` pair. We merge into the cached generation row
           * so the playground card can fill its skeleton grid in place.
           */
          partialOutputs?: Array<{ index: number; url: string }>;
          /**
           * Chain-orchestrator progress stage (W1 subject engine, spec §5.1).
           * Present only on intermediate 'processing' pushes for a
           * pinned/matched subject-composition video row. `sanitize-response.ts`
           * strips `chain_meta` from every customer HTTP response, so this
           * field arrives ONLY via WebSocket — never re-derivable from a
           * `['generations']` refetch. Written into the separate
           * generation-stage-store (NOT the Generation object / query cache)
           * so it survives the invalidateQueries calls below.
           */
          stage?: 'subject' | 'animating';
        };
      };

      if (!data.inference?.status) return;

      const { id, status, errorMessage, mediaType, partialOutputs, stage, outputUrls } =
        data.inference;

      if (id != null) {
        if (status === 'processing' && stage) {
          useGenerationStageStore.getState().setStage(id, stage);
        } else if (status === 'completed' || status === 'failed' || status === 'cancelled') {
          useGenerationStageStore.getState().clearStage(id);
        }
      }

      // Partial-output merge — runs regardless of status. The terminal
      // 'completed' event still arrives and overwrites with the full array.
      if (id != null && partialOutputs?.length) {
        type GenLike = {
          id: number;
          image_count?: number;
          output_urls?: Array<string | null> | string[];
        };
        type ListLike = GenerationRowsCache<GenLike>;
        // Plain list (getting-started checklist) AND infinite pages
        // (playground history) both live under the 'generations' prefix.
        queryClient.setQueriesData<ListLike>({ queryKey: ['generations'] }, (old) =>
          mapCachedGenerations<GenLike, ListLike>(old, (rows) => {
            if (!rows.some((gen) => gen.id === id)) return rows;
            return rows.map((gen) => {
              if (gen.id !== id) return gen;
              const highestIncoming = Math.max(...partialOutputs.map((p) => p.index));
              const total = Math.max(
                gen.image_count ?? 0,
                gen.output_urls?.length ?? 0,
                highestIncoming + 1,
              );
              const next: Array<string | null> = gen.output_urls
                ? [...(gen.output_urls as Array<string | null>)]
                : [];
              while (next.length < total) next.push(null);
              for (const { index, url } of partialOutputs) {
                next[index] = url;
              }
              return { ...gen, output_urls: next };
            });
          }),
        );
      }
      // Stories scene renders have their own stage and are not in playground history.
      if (isStoryInference(id)) return;

      const isOnPlayground = location.pathname === '/dashboard/playground';
      // Video pushes carry no mediaType; their output is the /v1/videos proxy URL.
      const isVideo = mediaType === 'video' || !!outputUrls?.[0]?.includes('/v1/videos/');

      // Is this generation's card on screen? Then the toast is redundant.
      // Browsing an earlier day (`?day=YYYY-MM-DD`, playground history-day.ts): the
      // new card is not in view, so the toast is the only completion signal.
      const dayParam = new URLSearchParams(location.search).get('day');
      const browsingEarlierDay = !!dayParam && dayParam !== format(new Date(), 'yyyy-MM-dd');
      const isLatestVisible = isOnPlayground && !browsingEarlierDay && isGenerationCardOnScreen(id);

      // Status pushes update the one row they concern. Invalidating the history
      // instead would re-request every page the user has loaded ("Load more"),
      // once per push, and a 4-image render pushes 5+ times.
      const refreshGenerations = () => {
        if (id != null) refreshAfterStatusPush(queryClient, id, status);
        else queryClient.invalidateQueries({ queryKey: ['generations'] });
      };

      if (status === 'completed' && id) {
        refreshGenerations();
        queryClient.invalidateQueries({ queryKey: ['teams'] });

        if (!isLatestVisible) {
          void (async () => {
            const text = isVideo ? 'Your video is ready!' : 'Your generation is ready.';
            // The pushed link is the provider's raw one, which the browser cannot load: the generation's own
            // links (as the playground shows them) are read once for the thumbnail.
            const generation = await generationService.getById(String(id)).catch(() => null);
            // A video shows from its video_url, as the playground's card does.
            const thumb =
              (isVideo ? generation?.video_url : generation?.output_urls?.find((u): u is string => typeof u === 'string')) ?? null;
            toast.success(isVideo ? 'Video generated' : 'Image generated', {
              // The output itself, small, beside the words (user, 2026-11-03).
              description: thumb ? (
                <span className="mt-1 flex items-center gap-2">
                  {isVideo ? (
                    <video
                      src={thumb}
                      muted
                      playsInline
                      preload="metadata"
                      className="h-12 w-12 shrink-0 rounded object-cover"
                    />
                  ) : (
                    <img src={thumb} alt="" className="h-12 w-12 shrink-0 rounded object-cover" />
                  )}
                  <span>{text}</span>
                </span>
              ) : (
                text
              ),
              action: {
                label: 'View',
                onClick: () => {
                  navigate(`/dashboard/playground?view=${id}`);
                },
              },
              duration: 8000,
            });
          })();
        }
      } else if (status === 'failed') {
        refreshGenerations();

        if (!isLatestVisible) {
          toast.error('Generation failed', {
            description: errorMessage || 'Something went wrong.',
            duration: 6000,
          });
        }
      } else if (status === 'processing') {
        refreshGenerations();
      }
    },
    [location.pathname, location.search, queryClient, navigate],
  );

  useWebSocketMessages({ enabled: isAuthenticated, onMessage: handleMessage });

  return null;
};
